import type { ComponentType } from 'react'
import { createBrowserRouter } from 'react-router-dom'

import { AppShell } from '@/components/layout/AppShell'
import {
  FINANCEIRO_CONSULTA,
  FINANCEIRO_ESCRITA,
  INVENTARIO_CONSULTA,
  INVENTARIO_GESTAO,
  INVENTARIO_MINHA_EQUIPE,
  OPERACIONAL_CALENDARIO,
  OPERACIONAL_PAINEL,
  OPERACIONAL_RELATORIOS,
  OPERACIONAL_PLANEJAMENTO,
  OPERACIONAL_REVISAO,
  RH_DASHBOARD,
  RH_DASHBOARD_ATESTADOS,
  ROLES,
} from '@/lib/auth/roles'
import { ProtectedRoute } from '@/routes/ProtectedRoute'
import { PublicOnlyRoute } from '@/routes/PublicOnlyRoute'
import { RequireRole } from '@/routes/RequireRole'
import { RootLayout } from '@/routes/RootLayout'
import { RouteErrorBoundary } from '@/routes/RouteErrorBoundary'

/** Helper: adapta um módulo com `export default` para o formato lazy do router. */
const lazyPage = (loader: () => Promise<{ default: ComponentType }>) => async () => ({
  Component: (await loader()).default,
})

export const router = createBrowserRouter([
  {
    // Layout raiz: envolve login e área autenticada para hospedar o card de
    // instalação do PWA num único ponto, acima dos guards.
    element: <RootLayout />,
    children: [
      {
        element: <PublicOnlyRoute />,
        errorElement: <RouteErrorBoundary />,
        children: [
          { path: '/login', lazy: lazyPage(() => import('@/features/auth/LoginPage')) },
          {
            path: '/esqueci-senha',
            lazy: lazyPage(() => import('@/features/auth/ForgotPasswordPage')),
          },
          {
            path: '/redefinir-senha',
            lazy: lazyPage(() => import('@/features/auth/ResetPasswordPage')),
          },
        ],
      },
      {
        element: <ProtectedRoute />,
        errorElement: <RouteErrorBoundary />,
        children: [
          {
            element: <AppShell />,
            children: [
              { index: true, lazy: lazyPage(() => import('@/features/home/HomePage')) },
              {
                path: 'meu-perfil',
                // `handle.valores`: a página mostra valores monetários (salário) e ganha o
                // botão "ocultar valores" no Topbar. Toda tela nova com valores deve marcar.
                handle: { valores: true },
                lazy: lazyPage(() => import('@/features/home/MyProfilePage')),
              },
              {
                path: 'contracheque',
                lazy: lazyPage(() => import('@/features/payslip/PayslipPage')),
              },
              {
                path: 'rh',
                // Dashboard: DIRETORIA e RH veem tudo; o SUPERVISOR_GERAL só o bloco de
                // atestados. O restante do módulo é DIRETORIA/RH, e o CRUD de colaboradores
                // e as configurações seguem RH-only via o guard aninhado abaixo.
                element: <RequireRole roles={RH_DASHBOARD_ATESTADOS} />,
                children: [
                  {
                    index: true,
                    handle: { valores: true },
                    lazy: lazyPage(() => import('@/features/rh/dashboard/RhDashboardPage')),
                  },
                  {
                    element: <RequireRole roles={RH_DASHBOARD} />,
                    children: [
                      {
                        path: 'Colaboradores',
                        // Lista ocupa toda a largura disponível no desktop (ver AppShell).
                        handle: { fullWidth: true },
                        lazy: lazyPage(() => import('@/features/rh/employees/EmployeeListPage')),
                      },
                      {
                        path: 'comunicados',
                        handle: { fullWidth: true },
                        lazy: lazyPage(
                          () => import('@/features/rh/comunicados/ComunicadoListPage'),
                        ),
                      },
                      {
                        path: 'comunicados/:id',
                        lazy: lazyPage(
                          () => import('@/features/rh/comunicados/ComunicadoDetailPage'),
                        ),
                      },
                      {
                        path: 'eventos',
                        handle: { fullWidth: true },
                        lazy: lazyPage(() => import('@/features/rh/eventos/EventoListPage')),
                      },
                      {
                        path: 'treinamentos',
                        handle: { fullWidth: true },
                        lazy: lazyPage(() => import('@/features/rh/treinamentos/TreinamentosPage')),
                      },
                      {
                        path: 'treinamentos/tipos',
                        lazy: lazyPage(
                          () => import('@/features/rh/treinamentos/TiposTreinamentoPage'),
                        ),
                      },
                      {
                        // Diretoria só vê a aba de treinamentos (ver detalheColaboradorAcesso).
                        path: 'Colaboradores/:id',
                        handle: { valores: true },
                        lazy: lazyPage(() => import('@/features/rh/employees/EmployeeDetailPage')),
                      },
                      {
                        path: 'ferias',
                        handle: { fullWidth: true },
                        lazy: lazyPage(() => import('@/features/rh/ferias/FeriasControlePage')),
                      },
                      {
                        path: 'eventos/:id',
                        lazy: lazyPage(() => import('@/features/rh/eventos/EventoDetailPage')),
                      },
                      {
                        path: 'comunicados/novo',
                        lazy: lazyPage(
                          () => import('@/features/rh/comunicados/ComunicadoCreatePage'),
                        ),
                      },
                      {
                        path: 'eventos/novo',
                        lazy: lazyPage(() => import('@/features/rh/eventos/EventoCreatePage')),
                      },
                      {
                        path: 'comunicados/:id/editar',
                        lazy: lazyPage(
                          () => import('@/features/rh/comunicados/ComunicadoEditPage'),
                        ),
                      },
                      {
                        path: 'eventos/:id/editar',
                        lazy: lazyPage(() => import('@/features/rh/eventos/EventoEditPage')),
                      },
                      {
                        element: <RequireRole roles={[ROLES.RH]} />,
                        children: [
                          {
                            path: 'Colaboradores/novo',
                            lazy: lazyPage(
                              () => import('@/features/rh/employees/EmployeeCreatePage'),
                            ),
                          },
                          {
                            path: 'Colaboradores/:id/editar',
                            lazy: lazyPage(
                              () => import('@/features/rh/employees/EmployeeEditPage'),
                            ),
                          },
                          {
                            path: 'configuracoes',
                            handle: { valores: true },
                            lazy: lazyPage(() => import('@/features/rh/lookups/TabelasApoioPage')),
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
              {
                path: 'inventario',
                // Gestão (DIRETORIA/RH/TI) e supervisores; as ações de gestão são
                // ocultadas na própria tela e revalidadas pela API (403).
                element: <RequireRole roles={INVENTARIO_CONSULTA} />,
                children: [
                  {
                    index: true,
                    handle: { fullWidth: true },
                    lazy: lazyPage(() => import('@/features/inventario/InventarioPage')),
                  },
                  {
                    path: 'itens/:id',
                    lazy: lazyPage(() => import('@/features/inventario/ItemDetailPage')),
                  },
                  {
                    // "Minha equipe": supervisores e Diretoria (o RH não acessa).
                    element: <RequireRole roles={INVENTARIO_MINHA_EQUIPE} />,
                    children: [
                      {
                        path: 'responsaveis',
                        handle: { fullWidth: true },
                        lazy: lazyPage(() => import('@/features/inventario/ResponsaveisPage')),
                      },
                    ],
                  },
                  {
                    element: <RequireRole roles={INVENTARIO_GESTAO} />,
                    children: [
                      {
                        path: 'categorias',
                        lazy: lazyPage(() => import('@/features/inventario/CategoriasPage')),
                      },
                    ],
                  },
                ],
              },
              {
                path: 'financeiro',
                // Consulta (FINANCEIRO/DIRETORIA/TI); as ações de escrita são ocultadas
                // na tela para a DIRETORIA e revalidadas pela API (403).
                handle: { valores: true },
                element: <RequireRole roles={FINANCEIRO_CONSULTA} />,
                children: [
                  {
                    index: true,
                    lazy: lazyPage(
                      () => import('@/features/financeiro/dashboard/DashboardFinanceiroPage'),
                    ),
                  },
                  {
                    path: 'contas-a-pagar',
                    handle: { fullWidth: true },
                    lazy: lazyPage(() => import('@/features/financeiro/titulos/ContasAPagarPage')),
                  },
                  {
                    path: 'contas-a-receber',
                    handle: { fullWidth: true },
                    lazy: lazyPage(
                      () => import('@/features/financeiro/titulos/ContasAReceberPage'),
                    ),
                  },
                  {
                    path: 'titulos/:id',
                    lazy: lazyPage(() => import('@/features/financeiro/titulos/TituloDetailPage')),
                  },
                  {
                    path: 'transferencias',
                    handle: { fullWidth: true },
                    lazy: lazyPage(
                      () => import('@/features/financeiro/transferencias/TransferenciasPage'),
                    ),
                  },
                  {
                    path: 'transferencias/:id',
                    lazy: lazyPage(
                      () => import('@/features/financeiro/transferencias/TransferenciaDetailPage'),
                    ),
                  },
                  {
                    path: 'reembolsos',
                    handle: { fullWidth: true },
                    lazy: lazyPage(() => import('@/features/financeiro/reembolsos/ReembolsosPage')),
                  },
                  {
                    path: 'reembolsos/:id',
                    lazy: lazyPage(
                      () => import('@/features/financeiro/reembolsos/ReembolsoDetailPage'),
                    ),
                  },
                  {
                    path: 'orcamentos',
                    handle: { fullWidth: true },
                    lazy: lazyPage(() => import('@/features/financeiro/orcamentos/OrcamentosPage')),
                  },
                  {
                    path: 'orcamentos/:id',
                    lazy: lazyPage(
                      () => import('@/features/financeiro/orcamentos/OrcamentoDetailPage'),
                    ),
                  },
                  {
                    // Telas só de escrita: a DIRETORIA (consulta) é redirecionada para /403.
                    element: <RequireRole roles={FINANCEIRO_ESCRITA} />,
                    children: [
                      {
                        path: 'reembolsos/novo',
                        lazy: lazyPage(
                          () => import('@/features/financeiro/reembolsos/ReembolsoFormPage'),
                        ),
                      },
                      {
                        path: 'reembolsos/:id/editar',
                        lazy: lazyPage(
                          () => import('@/features/financeiro/reembolsos/ReembolsoFormPage'),
                        ),
                      },
                      {
                        path: 'reembolsos/:id/itens',
                        lazy: lazyPage(
                          () => import('@/features/financeiro/reembolsos/ReembolsoItensPage'),
                        ),
                      },
                      {
                        path: 'orcamentos/novo',
                        lazy: lazyPage(
                          () => import('@/features/financeiro/orcamentos/OrcamentoFormPage'),
                        ),
                      },
                      {
                        path: 'orcamentos/:id/editar',
                        lazy: lazyPage(
                          () => import('@/features/financeiro/orcamentos/OrcamentoFormPage'),
                        ),
                      },
                    ],
                  },
                  {
                    path: 'clientes',
                    handle: { fullWidth: true },
                    lazy: lazyPage(() => import('@/features/financeiro/clientes/ClientesPage')),
                  },
                  {
                    path: 'clientes/:id',
                    lazy: lazyPage(
                      () => import('@/features/financeiro/clientes/ClienteDetailPage'),
                    ),
                  },
                  {
                    path: 'contratos',
                    handle: { fullWidth: true },
                    lazy: lazyPage(() => import('@/features/financeiro/clientes/ContratosPage')),
                  },
                  {
                    path: 'contratos/:id',
                    lazy: lazyPage(
                      () => import('@/features/financeiro/clientes/ContratoDetailPage'),
                    ),
                  },
                  {
                    path: 'fornecedores',
                    handle: { fullWidth: true },
                    lazy: lazyPage(
                      () => import('@/features/financeiro/fornecedores/FornecedoresPage'),
                    ),
                  },
                  {
                    path: 'fornecedores/:id',
                    lazy: lazyPage(
                      () => import('@/features/financeiro/fornecedores/FornecedorDetailPage'),
                    ),
                  },
                  {
                    path: 'configuracoes',
                    lazy: lazyPage(
                      () =>
                        import('@/features/financeiro/configuracoes/ConfiguracoesFinanceiroPage'),
                    ),
                  },
                ],
              },
              {
                path: 'operacional',
                // Painel e detalhe da visita: planejador, analista e Diretoria (só leitura).
                // As ações de escrita são ocultadas por papel e revalidadas pela API (403).
                element: <RequireRole roles={OPERACIONAL_PAINEL} />,
                children: [
                  {
                    index: true,
                    handle: { fullWidth: true },
                    lazy: lazyPage(
                      () => import('@/features/operacional/dashboard/PainelOperacionalPage'),
                    ),
                  },
                  {
                    path: 'visitas/:id',
                    lazy: lazyPage(
                      () => import('@/features/operacional/planejamento/VisitaDetailPage'),
                    ),
                  },
                  {
                    // Consulta para planejador, analista e Diretoria; a escrita é ocultada
                    // por papel da ação e revalidada pela API (403).
                    element: <RequireRole roles={OPERACIONAL_CALENDARIO} />,
                    children: [
                      {
                        path: 'calendario',
                        handle: { fullWidth: true },
                        lazy: lazyPage(
                          () => import('@/features/operacional/planejamento/CalendarioPage'),
                        ),
                      },
                    ],
                  },
                  {
                    element: <RequireRole roles={OPERACIONAL_PLANEJAMENTO} />,
                    children: [
                      {
                        path: 'roteiros',
                        handle: { fullWidth: true },
                        lazy: lazyPage(
                          () => import('@/features/operacional/roteiros/RoteirosPage'),
                        ),
                      },
                      {
                        path: 'unidades',
                        handle: { fullWidth: true },
                        lazy: lazyPage(
                          () => import('@/features/operacional/unidades/UnidadesPage'),
                        ),
                      },
                      {
                        path: 'equipes',
                        lazy: lazyPage(
                          () => import('@/features/operacional/equipes/EquipesOperacionalPage'),
                        ),
                      },
                    ],
                  },
                  {
                    element: <RequireRole roles={OPERACIONAL_RELATORIOS} />,
                    children: [
                      {
                        path: 'relatorios',
                        handle: { fullWidth: true },
                        lazy: lazyPage(
                          () => import('@/features/operacional/relatorios/RelatoriosPage'),
                        ),
                      },
                    ],
                  },
                  {
                    element: <RequireRole roles={OPERACIONAL_REVISAO} />,
                    children: [
                      {
                        path: 'revisao',
                        handle: { fullWidth: true },
                        lazy: lazyPage(() => import('@/features/operacional/revisao/RevisaoPage')),
                      },
                      {
                        path: 'revisao/:id',
                        handle: { fullWidth: true },
                        lazy: lazyPage(() => import('@/features/operacional/revisao/RevisaoPage')),
                      },
                    ],
                  },
                ],
              },
              {
                // Responsável da equipe (a API filtra as visitas pela equipe do usuário).
                path: 'minhas-visitas',
                lazy: lazyPage(
                  () => import('@/features/operacional/minhasVisitas/MinhasVisitasPage'),
                ),
              },
              {
                path: 'minhas-visitas/:id',
                lazy: lazyPage(
                  () => import('@/features/operacional/minhasVisitas/ExecucaoVisitaPage'),
                ),
              },
              {
                path: 'minhas-visitas/:id/etapas/:etapaId',
                lazy: lazyPage(() => import('@/features/operacional/minhasVisitas/EtapaPage')),
              },
              {
                // Qualquer colaborador: itens sob a própria responsabilidade.
                path: 'meus-itens',
                lazy: lazyPage(() => import('@/features/inventario/MeusItensPage')),
              },
              {
                path: 'meus-treinamentos',
                lazy: lazyPage(() => import('@/features/treinamentos/MeusTreinamentosPage')),
              },
              {
                path: 'meus-atestados',
                lazy: lazyPage(() => import('@/features/atestados/MeusAtestadosPage')),
              },
              {
                path: 'meus-avisos-ferias',
                lazy: lazyPage(() => import('@/features/ferias/MeusAvisosFeriasPage')),
              },
              {
                path: 'meus-comunicados',
                lazy: lazyPage(() => import('@/features/comunicados/MeusComunicadosPage')),
              },
              {
                path: 'meus-eventos',
                lazy: lazyPage(() => import('@/features/eventos/MeusEventosPage')),
              },
              {
                path: 'atendimento',
                lazy: lazyPage(() => import('@/features/atendimento/AtendimentoPage')),
                handle: { fullWidth: true },
              },
              {
                path: 'atendimento/nova',
                lazy: lazyPage(() => import('@/features/atendimento/NovaSolicitacaoPage')),
              },
              {
                path: 'atendimento/:id',
                lazy: lazyPage(() => import('@/features/atendimento/SolicitacaoDetailPage')),
              },
              { path: '403', lazy: lazyPage(() => import('@/routes/ForbiddenPage')) },
              { path: '*', lazy: lazyPage(() => import('@/routes/NotFoundPage')) },
            ],
          },
        ],
      },
    ],
  },
])
